import { useEffect, useState } from 'react';
import InstitutionMark, { effectiveInstitutionLogoSource } from './InstitutionMark.jsx';
import { useTokens } from './theme.jsx';
import { CheckGlyph } from './uiKit.jsx';

export default function InstitutionLogoPicker({
  name,
  slug,
  logoFileId,
  logoSource,
  autoLogoDomain,
  busy = false,
  onSelect,
  onUpload,
  uploading = false,
}) {
  const t = useTokens();
  const activeSource = effectiveInstitutionLogoSource(logoSource, logoFileId);
  // Set when the uploaded logo image fails to load; a new upload clears it.
  const [uploadedFailed, setUploadedFailed] = useState(false);
  useEffect(() => { setUploadedFailed(false); }, [logoFileId, slug]);
  const cardClass = `min-h-[92px] rounded-lg border p-3 text-left transition-opacity ${t.focusRing}`;
  const options = [
    {
      source: 'AUTO',
      title: 'Automatic school logo',
      detail: autoLogoDomain || 'Add a verified domain to enable',
      available: Boolean(autoLogoDomain),
    },
    {
      source: 'UPLOADED',
      title: 'Uploaded override',
      detail: uploadedFailed
        ? 'Your uploaded logo could not be loaded. Replace it or switch to Automatic.'
        : (logoFileId ? 'Your retained custom logo' : 'Upload a logo to enable'),
      available: Boolean(logoFileId),
    },
  ];

  const renderUploadCard = () => {
    return (
      <label
        key="UPLOADED"
        className={`${cardClass} flex cursor-pointer items-center focus-within:ring-2 focus-within:ring-offset-2 ${uploading || busy ? 'cursor-not-allowed opacity-55' : 'hover:opacity-85'}`}
        style={{ backgroundColor: t.surface, borderColor: t.border, borderStyle: 'dashed' }}
      >
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="sr-only"
          disabled={uploading || busy}
          onChange={onUpload}
        />
        <div className="flex items-center gap-3">
          <InstitutionMark
            name={name}
            slug={slug}
            logoFileId={null}
            logoSource="UPLOADED"
            autoLogoDomain={autoLogoDomain}
            size={52}
          />
          <div className="min-w-0">
            <div className="text-sm font-semibold" style={{ color: t.heading, fontFamily: t.fonts.body }}>
              Custom logo
            </div>
            <div className="mt-1 text-[13px]" style={{ color: t.muted, fontFamily: t.fonts.mono }}>
              PNG, JPEG, or WebP · max 5 MB
            </div>
            <div className="mt-1 text-[13px]" style={{ color: t.muted, fontFamily: t.fonts.body }}>
              {uploading ? 'Uploading...' : 'Upload a logo'}
            </div>
          </div>
        </div>
      </label>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" aria-label="Active institution logo">
      {options.map((option) => {
        // With an upload handler wired, the empty override slot becomes the
        // upload affordance itself instead of a dead "Unavailable" card, so
        // the admin can always move between the automatic and custom logo.
        if (option.source === 'UPLOADED' && !logoFileId && onUpload) {
          return renderUploadCard();
        }
        const active = activeSource === option.source;
        return (
          <button
            key={option.source}
            type="button"
            aria-pressed={active}
            disabled={busy || !option.available}
            onClick={() => { if (!active && option.available) { onSelect?.(option.source); } }}
            className={`${cardClass} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${busy || !option.available ? 'cursor-not-allowed opacity-55' : 'hover:opacity-85'}`}
            style={{
              backgroundColor: active ? t.statBg : t.surface,
              borderColor: active ? t.heading : t.controlBorder,
            }}
          >
            <div className="flex items-center gap-3">
              {/* shrink-0: the mark's min-w-0 root would otherwise collapse under
                  the long failed-logo sentence and the crest would cover it. */}
              <div className="shrink-0">
                <InstitutionMark
                  name={name}
                  slug={slug}
                  logoFileId={logoFileId}
                  logoSource={option.source}
                  autoLogoDomain={autoLogoDomain}
                  size={52}
                  onLogoError={option.source === 'UPLOADED' ? () => { setUploadedFailed(true); } : undefined}
                />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold" style={{ color: t.heading, fontFamily: t.fonts.body }}>
                  {option.title}
                </div>
                {/* Wraps instead of truncating, so the full detail stays readable. */}
                {/* The failed-logo sentence is body copy; mono stays for the short cue. */}
                <div
                  className="mt-1 break-words text-[13px]"
                  style={option.source === 'UPLOADED' && uploadedFailed
                    ? { color: t.danger, fontFamily: t.fonts.body }
                    : { color: t.muted, fontFamily: t.fonts.mono }}
                >
                  {option.detail}
                </div>
                {/* The active card carries a check and a word, not fill alone. */}
                <div
                  className="mt-1 inline-flex items-center text-[13px]"
                  style={{ color: active ? t.heading : t.muted, fontFamily: t.fonts.body }}
                >
                  {active ? <CheckGlyph /> : null}
                  {active ? 'Active' : option.available ? 'Use this logo' : 'Unavailable'}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
